/**
 * CR221: Builder Dashboard
 * 
 * Landing hub for the Builder workflow (top-down):
 * Layout Studio → Theme Labs → Deal Cards → Asset Studio → Publish
 */

import { useMemo } from "react";
import { Link } from "react-router-dom";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Sparkles,
  CreditCard,
  Palette,
  LayoutGrid,
  Rocket,
  ArrowRight,
  CheckCircle2,
  Clock,
  AlertTriangle,
  Image,
} from "lucide-react";
import { useAppAssets } from "@/hooks/useAppAssets";
import { useLayoutStudio } from "@/hooks/useLayoutStudio";

interface WorkflowCardProps {
  title: string;
  description: string;
  href: string;
  icon: React.ReactNode;
  status: "ready" | "attention" | "incomplete";
  statusText: string;
  metrics?: { label: string; value: string | number }[];
  order: number;
}

function WorkflowCard({ title, description, href, icon, status, statusText, metrics, order }: WorkflowCardProps) {
  const statusVariant = status === "ready" ? "default" : status === "attention" ? "warning" : "outline";
  const StatusIcon = status === "ready" ? CheckCircle2 : status === "attention" ? AlertTriangle : Clock;
  
  return (
    <Card className="relative overflow-hidden hover:shadow-md transition-shadow">
      {/* Order indicator */}
      <div className="absolute top-3 right-3 w-6 h-6 rounded-full bg-muted flex items-center justify-center text-xs font-medium text-muted-foreground">
        {order}
      </div>
      
      <CardHeader className="pb-2">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary/10 rounded-lg">
            {icon}
          </div>
          <div>
            <CardTitle className="text-base">{title}</CardTitle>
            <CardDescription className="text-xs">{description}</CardDescription>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Status */}
        <div className="flex items-center gap-2">
          <StatusIcon className={`h-4 w-4 ${
            status === "ready" ? "text-primary" : 
            status === "attention" ? "text-warning" : 
            "text-muted-foreground"
          }`} />
          <Badge variant={statusVariant} className="text-[10px]">
            {statusText}
          </Badge>
        </div>
        
        {/* Metrics */}
        {metrics && metrics.length > 0 && (
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
            {metrics.map((m, i) => (
              <span key={i}>
                <span className="font-medium text-foreground">{m.value}</span> {m.label}
              </span>
            ))}
          </div>
        )}
        
        {/* Action */}
        <Link to={href}>
          <Button variant="outline" size="sm" className="w-full group">
            Open
            <ArrowRight className="h-3 w-3 ml-2 group-hover:translate-x-1 transition-transform" />
          </Button>
        </Link>
      </CardContent>
    </Card>
  );
}

export default function BuilderDashboard() {
  // Fetch data for status
  const { assets, loading: assetsLoading } = useAppAssets({ status: "active" });
  const { presets, activations, loading: layoutLoading } = useLayoutStudio();
  
  // Compute status for each workflow section
  const workflowStatus = useMemo(() => {
    const logoCount = assets?.filter(a => a.tags?.includes("logo")).length || 0;
    const iconCount = assets?.filter(a => a.type === "starburst" || a.type === "icon").length || 0;
    
    // Layout Studio status
    const stagingActivation = activations.find(a => a.environment === "staging");
    const liveActivation = activations.find(a => a.environment === "publish");
    const hasLivePreset = !!liveActivation?.active_preset_id;
    const hasStagingPreset = !!stagingActivation?.active_preset_id;
    
    return {
      assets: {
        status: (logoCount > 0 ? "ready" : "incomplete") as "ready" | "attention" | "incomplete",
        statusText: logoCount > 0 ? `${assets?.length || 0} assets ready` : "No logo uploaded",
        metrics: [
          { label: "logos", value: logoCount },
          { label: "icons", value: iconCount },
          { label: "total", value: assets?.length || 0 },
        ],
      },
      dealCards: {
        status: "ready" as const,
        statusText: "Templates configured",
        metrics: [
          { label: "deal types", value: 5 },
        ],
      },
      themeLabs: {
        status: "ready" as const,
        statusText: "Tokens defined",
        metrics: [],
      },
      layoutStudio: {
        status: (hasLivePreset ? "ready" : hasStagingPreset ? "attention" : "incomplete") as "ready" | "attention" | "incomplete",
        statusText: hasLivePreset 
          ? "Published to Live" 
          : hasStagingPreset 
            ? "Staging only" 
            : "No preset active",
        metrics: [
          { label: "presets", value: presets.length },
        ],
      },
    };
  }, [assets, presets, activations]);
  
  const isLoading = assetsLoading || layoutLoading;

  return (
    <AdminLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <LayoutGrid className="h-6 w-6 text-primary" />
            Builder Dashboard
          </h1>
          <p className="text-muted-foreground">
            Build your Deal Finder experience from assets to published layout
          </p>
        </div>
        
        {/* Workflow Progress */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Workflow Progress</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <Progress 
                value={
                  ((workflowStatus.assets.status === "ready" ? 25 : 0) +
                  (workflowStatus.dealCards.status === "ready" ? 25 : 0) +
                  (workflowStatus.themeLabs.status === "ready" ? 25 : 0) +
                  (workflowStatus.layoutStudio.status === "ready" ? 25 : 0))
                } 
                className="flex-1" 
              />
              <span className="text-sm text-muted-foreground whitespace-nowrap">
                {[
                  workflowStatus.assets.status === "ready",
                  workflowStatus.dealCards.status === "ready",
                  workflowStatus.themeLabs.status === "ready",
                  workflowStatus.layoutStudio.status === "ready",
                ].filter(Boolean).length} / 4 complete
              </span>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Follow the workflow: Layout Studio → Theme Labs → Deal Cards → Asset Studio
            </p>
          </CardContent>
        </Card>
        
        {/* Workflow Cards - Top-down order: Layout → Theme → Cards → Assets */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <WorkflowCard
            order={1}
            title="Layout Studio"
            description="Assemble the page (header, filters, grid, preview source)"
            href="/admin/layout-studio"
            icon={<LayoutGrid className="h-5 w-5 text-primary" />}
            status={workflowStatus.layoutStudio.status}
            statusText={workflowStatus.layoutStudio.statusText}
            metrics={workflowStatus.layoutStudio.metrics}
          />
          
          <WorkflowCard
            order={2}
            title="Theme Labs"
            description="Global tokens (fonts, colors, radius, shadows)"
            href="/admin/theme-labs"
            icon={<Palette className="h-5 w-5 text-primary" />}
            status={workflowStatus.themeLabs.status}
            statusText={workflowStatus.themeLabs.statusText}
            metrics={workflowStatus.themeLabs.metrics}
          />
          
          <WorkflowCard
            order={3}
            title="Deal Cards"
            description="Templates + blocks + attribute rules"
            href="/admin/command-center"
            icon={<CreditCard className="h-5 w-5 text-primary" />}
            status={workflowStatus.dealCards.status}
            statusText={workflowStatus.dealCards.statusText}
            metrics={workflowStatus.dealCards.metrics}
          />
          
          <WorkflowCard
            order={4}
            title="Asset Studio"
            description="Upload/manage assets + tag/validate SVG"
            href="/admin/asset-studio"
            icon={<Sparkles className="h-5 w-5 text-primary" />}
            status={workflowStatus.assets.status}
            statusText={workflowStatus.assets.statusText}
            metrics={workflowStatus.assets.metrics}
          />
        </div>
        
        {/* Quick Actions */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-2">
            <Link to="/admin/layout-studio">
              <Button size="sm" className="gap-2">
                <LayoutGrid className="h-4 w-4" />
                Go to Layout Studio
              </Button>
            </Link>
            <Link to="/admin/live-preview">
              <Button variant="outline" size="sm" className="gap-2">
                <Rocket className="h-4 w-4" />
                View Live Preview
              </Button>
            </Link>
            <Link to="/admin/asset-studio">
              <Button variant="outline" size="sm" className="gap-2">
                <Image className="h-4 w-4" />
                Upload Assets
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
